import { FC } from 'react';
import { useQueryClient } from 'react-query';
import { Rating } from '@mui/material';
import { FormikHelpers } from 'formik';
import {
  ButtonOutlinePrimary,
  Form,
  Icon,
  InputTextarea,
  Modal,
  ModalHeader,
  ModalProps
} from '@lambdacurry/component-library';
import { MyOrdersQuery, SaveProductRatingInput } from 'lib/home/generated/types/api';
import { useSaveFavoritePreferenceMutation } from 'lib/home/generated/queries';

export interface ProductRatingModalProps extends ModalProps {
  isOpen: boolean;
  ratingInfo: RatingInfoProps;
  saveProductRating: (props: SaveProductRatingInput) => void;
}

export interface RatingInfoProps {
  title: string;
  medusa_line_item_id: string;
  medusa_order_id: string;
  variant_id: string;
  variant_preference: {
    id: string;
    favorite?: boolean;
    never?: boolean;
  };
  rating?: string;
  note?: string;
}

export const ProductRatingModal: FC<ProductRatingModalProps> = ({
  isOpen,
  ratingInfo,
  saveProductRating,
  ...props
}) => {
  const queryClient = useQueryClient();

  const handleSubmit = (values: FormValues, formikHelpers: FormikHelpers<FormValues>) => {
    saveProductRating({
      medusa_order_id: ratingInfo.medusa_order_id,
      medusa_line_item_id: ratingInfo.medusa_line_item_id,
      rating: parseInt(values.rating),
      note: values.note
    });
  };

  const { mutate: mutateFavoritePreference } = useSaveFavoritePreferenceMutation({
    onSuccess: ({ saveFavoritePreference }) => {
      queryClient.setQueryData(['MyOrders'], (myOrdersQuery: MyOrdersQuery) => {
        const orderIndex = myOrdersQuery.customerOrders.findIndex(order => order.id === ratingInfo.medusa_order_id);

        const itemIndex = myOrdersQuery.customerOrders[orderIndex].cart.items.findIndex(
          item => item.id === ratingInfo.medusa_line_item_id
        );

        myOrdersQuery.customerOrders[orderIndex].cart.items[itemIndex].variant.variant_preference =
          saveFavoritePreference;

        return myOrdersQuery;
      });
      ratingInfo.variant_preference = saveFavoritePreference;
    }
  });

  const initialValues = { rating: ratingInfo?.rating, note: ratingInfo?.note };
  type FormValues = typeof initialValues;

  return (
    <Modal id={`rating-form-modal`} isOpen={isOpen} {...props}>
      <ModalHeader title={ratingInfo?.title} />
      <Form<FormValues> initialValues={initialValues} onSubmit={handleSubmit}>
        {formikProps => (
          <>
            <h4>How Would You Rate This Dish?</h4>
            <Rating
              data-cy="rating-input"
              name="rating"
              value={parseFloat(formikProps.values.rating)}
              onChange={value => formikProps.handleChange(value)}
            />
            <InputTextarea
              className="mt-2"
              data-cy="note-input"
              name="note"
              label="write your message here ..."
              rows={8}
            />

            <div className="flex justify-end mt-4">
              <ButtonOutlinePrimary disabled={!formikProps.values.rating} data-cy="submit" type="submit">
                Submit
              </ButtonOutlinePrimary>
            </div>
          </>
        )}
      </Form>
      <div>
        {!ratingInfo?.variant_preference?.favorite ? (
          <>
            <h4>Interested In This Dish Again?</h4>
            <div>Add it to your favorites, so you can find it easily.</div>
            <ButtonOutlinePrimary
              className="mt-2"
              data-cy="favorite"
              onClick={() =>
                mutateFavoritePreference({
                  saveFavoritePreference: { product_variant_id: ratingInfo.variant_id, favorite: true }
                })
              }
            >
              <Icon className="mr-2" name="heart" />
              Add to my favorites
            </ButtonOutlinePrimary>
          </>
        ) : (
          <div className="flex" data-cy="favored">
            <Icon className="mr-2 text-brand" name="heartFilled" />
            <div>
              <p className="m-0 font-medium">It's A Hit!</p>
              <p className="m-0">
                You can find it in your <a href="">favorites</a>
              </p>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};
